import { stateAbbreviations } from 'constants/enum';
import { arrayDifference } from 'helpers/arrays';
import {
  getCarrierStateOptions,
  getConfirmedStates,
  getPendingStates,
  isAgentAppointedWithCarrier
} from 'helpers/appointments/appointments';
import { SBE_STATES } from 'helpers/appointments/ffmCertification';
import { getStateCodeFromStateName } from 'helpers/states';
import { getCompany } from 'helpers/getCompanyVariables';
import { slugifyTabId } from 'lib/tabs';

/** Status ids for carrier + state on maps and by-state sidebars (not appointment dropdowns). */
export const CARRIER_STATE_DISPLAY_STATUS = {
  CONFIRMED: 'confirmed',
  PENDING: 'pending',
  PENDING_AGENT_ACTION: 'pending-agent-action',
  NOT_APPOINTED: 'not-appointed',
  OFFERED_NOT_AVAILABLE: 'offered-not-available'
};

const company = getCompany();

// Carrier Appointments Group value that marks a carrier as off-market (a subgroup of Health)
const OFF_MARKET_GROUP = 'Off-Market';

export const AGENCY_DISABLED_CARRIER_MESSAGE =
  'Looking to expand your carrier options? Reach out to your upline or agency admin about access to this carrier.';

export function isCarrierDisabledByAgencyPreferences(carrier, user) {
  if (company === 'sunshine') {
    if (
      carrier.type === 'Off-Market Health' ||
      carrier.Appointment_Type === 'Off-Market Health'
    ) {
      return !!user?.agencyData?.Hide_Off_Market;
    }
    return false;
  }

  if (carrier.disabledByUpline || carrier.Disabled_By_Upline) return true;

  if (
    carrier.appointmentGroup === OFF_MARKET_GROUP ||
    carrier.Carrier_Appointments_Group === OFF_MARKET_GROUP
  )
    return !!user?.agencyData?.Hide_Off_Market;

  if (
    carrier.appointmentParentGroup === 'Ancillary Appointments' ||
    carrier.Carrier_Appointments_Parent_Group === 'Ancillary Appointments'
  )
    return !!user?.agencyData?.Hide_Ancillary;

  return false;
}

export const VALID_STATE_CODES = new Set([
  ...Object.values(stateAbbreviations),
  'DC'
]);

/**
 * Normalizes carrier `statesOffered` (array or comma-separated string) to 2-letter codes.
 */
export function normalizeStatesOfferedToCodes(statesOffered) {
  if (!statesOffered) return [];
  const raw = Array.isArray(statesOffered)
    ? statesOffered
    : String(statesOffered).split(/[,;]/);
  const codes = new Set();
  for (const item of raw) {
    if (item == null || item === '') continue;
    const s = String(item).trim();
    if (!s) continue;
    const upper = s.toUpperCase();
    if (upper.length === 2 && VALID_STATE_CODES.has(upper)) {
      codes.add(upper);
      continue;
    }
    const fromName = getStateCodeFromStateName(s);
    if (fromName && VALID_STATE_CODES.has(fromName)) codes.add(fromName);
  }
  return [...codes];
}

export function statesOfferedToArray(statesOffered) {
  if (!statesOffered) return [];
  if (Array.isArray(statesOffered)) {
    return statesOffered.filter(s => s != null && String(s).trim() !== '');
  }
  return String(statesOffered)
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);
}

function carrierVisibleForStateMap(carrier, user) {
  if (isCarrierDisabledByAgencyPreferences(carrier, user)) {
    return true;
  }
  return (
    !carrier.appointmentsDisabled ||
    isAgentAppointedWithCarrier(carrier.name, user)
  );
}

/** State codes where the carrier should appear on the by-state map (all offered states; no license filter). */
function getEligibleStateCodesForByStateMap(
  carrier,
  user,
  requestedAppointments
) {
  const { statesOffered, name } = carrier;
  const offered = statesOfferedToArray(statesOffered);
  if (!offered.length) return [];

  if (isCarrierDisabledByAgencyPreferences(carrier, user)) {
    return normalizeStatesOfferedToCodes(statesOffered);
  }

  const confirmed = getConfirmedStates(name, user);

  if (carrier.appointmentsDisabled && isAgentAppointedWithCarrier(name, user)) {
    return confirmed
      .map(stateName => getStateCodeFromStateName(stateName))
      .filter(Boolean);
  }

  const pending = getPendingStates(
    requestedAppointments.filter(appt => appt.carrier === name),
    confirmed
  );

  const tempOptions = arrayDifference(offered, [...confirmed, ...pending]);

  const eligibleStateNames = [...confirmed, ...pending, ...tempOptions];

  return eligibleStateNames
    .map(stateName => getStateCodeFromStateName(stateName))
    .filter(Boolean);
}

/**
 * Resolves display status for a carrier in one state (maps / by-state lists).
 * Dropdowns still use `getCarrierStateOptions`, which excludes states the agent
 * can't request (no license, no valid FFM certificate, carrier restrictions).
 */
export function getCarrierStateDisplayStatus(
  carrier,
  stateName,
  user,
  requestedAppointments = [],
  actionableAppointments = []
) {
  if (!carrier || !stateName) {
    return CARRIER_STATE_DISPLAY_STATUS.NOT_APPOINTED;
  }

  const offered = statesOfferedToArray(carrier.statesOffered);
  if (!offered.includes(stateName)) {
    return CARRIER_STATE_DISPLAY_STATUS.NOT_APPOINTED;
  }

  const { name } = carrier;
  const confirmed = getConfirmedStates(name, user) ?? [];
  const confirmedArr = Array.isArray(confirmed) ? confirmed : [];

  const actionablePending = getPendingStates(
    actionableAppointments.filter(a => a.carrier === name),
    confirmedArr
  );
  if (actionablePending.includes(stateName)) {
    return CARRIER_STATE_DISPLAY_STATUS.PENDING_AGENT_ACTION;
  }
  if (confirmedArr.includes(stateName)) {
    return CARRIER_STATE_DISPLAY_STATUS.CONFIRMED;
  }

  const pending = getPendingStates(
    requestedAppointments.filter(a => a.carrier === name),
    confirmedArr
  ).filter(s => !actionablePending.includes(s));
  if (pending.includes(stateName)) {
    return CARRIER_STATE_DISPLAY_STATUS.PENDING;
  }

  const tempOptions = arrayDifference(offered, [...confirmedArr, ...pending]);
  const requestableOptions = getCarrierStateOptions(carrier, user, tempOptions);
  if (requestableOptions.includes(stateName)) {
    return CARRIER_STATE_DISPLAY_STATUS.NOT_APPOINTED;
  }
  if (tempOptions.includes(stateName)) {
    return CARRIER_STATE_DISPLAY_STATUS.OFFERED_NOT_AVAILABLE;
  }

  return CARRIER_STATE_DISPLAY_STATUS.NOT_APPOINTED;
}

/**
 * Builds `{ [stateCode]: { SBE, carriers: string[] } }` for the by-state map.
 * Same visibility rules as the carrier grid (disabled carriers hidden unless appointed).
 * Per carrier, all `statesOffered` entries are included (confirmed, pending, and
 * remaining tempOptions). License/FFM filtering applies only in appointment request dropdowns.
 */
export function getCarrierPartnersMapFromCarriers(
  carriersArray,
  user,
  requestedAppointments = []
) {
  const byState = {};
  const carriers = (carriersArray || []).filter(
    c => c.appointmentParentGroup && carrierVisibleForStateMap(c, user)
  );

  for (const carrier of carriers) {
    const eligibleCodes = getEligibleStateCodesForByStateMap(
      carrier,
      user,
      requestedAppointments
    );
    const { name } = carrier;

    for (const code of eligibleCodes) {
      if (!byState[code]) {
        byState[code] = {
          SBE: SBE_STATES.includes(code),
          carriers: []
        };
      }
      if (!byState[code].carriers.includes(name)) {
        byState[code].carriers.push(name);
      }
    }
  }

  Object.values(byState).forEach(s =>
    s.carriers.sort((a, b) => a.localeCompare(b))
  );

  return byState;
}

/** Fills in every US map code so the map can color SBE vs FFM for all states. */
export function getFullStateMapForDisplay(byStateCarriers) {
  const result = {};
  for (const code of VALID_STATE_CODES) {
    result[code] = {
      SBE: SBE_STATES.includes(code),
      carriers: byStateCarriers[code]?.carriers ?? []
    };
  }
  return result;
}

/** Default Carrier Appointments / Resources tab: all parent groups, carriers limited to Redux appointed set. */
export const APPOINTED_CARRIERS_TAB_ID = 'appointed-carriers';

/** Flatten `appointedCarriers` keyed by marketplace type into a Set of carrier names. */
export function getAppointedCarrierNameSet(appointedCarriers) {
  const names = new Set();
  if (!appointedCarriers || typeof appointedCarriers !== 'object') return names;
  for (const byType of Object.values(appointedCarriers)) {
    if (byType && typeof byType === 'object') {
      for (const name of Object.keys(byType)) {
        names.add(name);
      }
    }
  }
  return names;
}

// CRM carrier-type picklist values all end in this suffix (e.g. Off-Market Health Appointments)
const CARRIER_TYPE_SUFFIX = ' Appointments';

// a parent group's display label (drops the trailing " Appointments")
export function getParentGroupLabel(appointmentParentGroup) {
  const group = appointmentParentGroup || '';
  return group.endsWith(CARRIER_TYPE_SUFFIX)
    ? group.slice(0, -CARRIER_TYPE_SUFFIX.length)
    : group;
}

// legacy "Off-Market Health" naming; folded into the single Off-Market type
const OFF_MARKET_LEGACY_LABEL = 'Off-Market Health';

function isOffMarketValue(value) {
  const label = getParentGroupLabel(value);
  return label === OFF_MARKET_GROUP || label === OFF_MARKET_LEGACY_LABEL;
}

// display label for a group value, with every off-market variant shown as Off-Market
function getTypeLabel(value) {
  return isOffMarketValue(value)
    ? OFF_MARKET_GROUP
    : getParentGroupLabel(value);
}

/**
 * carrier's type for the by-state map filter: its appointments group when that
 * group is itself a type (a subgroup of Health like Dental, or a full CRM value), else its
 * parent group; every off-market variant (Off-Market, Off-Market Health Appointments) is one type
 * @param carrier - carrier object from the carriers factory
 */
export function getCarrierType(carrier) {
  const group = String(carrier?.appointmentGroup ?? '').trim();
  const parent = String(carrier?.appointmentParentGroup ?? '').trim();
  if (isOffMarketValue(group) || isOffMarketValue(parent)) {
    return OFF_MARKET_GROUP;
  }
  if (group.endsWith(CARRIER_TYPE_SUFFIX)) return group;
  return parent;
}

/**
 * alphabetical, de-duped `[{ value, label }]` carrier types present in the carriers array
 * @param carriersArray - carriers from the appointments store
 */
export function getCarrierTypeOptions(carriersArray) {
  const types = new Set(
    (carriersArray || [])
      .filter(c => !!c.appointmentParentGroup)
      .map(getCarrierType)
      .filter(Boolean)
  );
  return [...types]
    .map(type => ({ value: type, label: getTypeLabel(type) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * keeps only carriers whose type is selected; an empty selection keeps every carrier
 * @param carriersArray - carriers from the appointments store
 * @param selectedTypes - carrier type values from `getCarrierTypeOptions`
 */
export function filterCarriersByType(carriersArray, selectedTypes) {
  if (!selectedTypes?.length) return carriersArray || [];
  return (carriersArray || []).filter(c =>
    selectedTypes.includes(getCarrierType(c))
  );
}

/**
 * category label like "Health · Off-Market": the parent group, plus the group when it adds something
 * @param carrier - carrier object from the carriers factory
 */
export function getCarrierCategoryLabel(carrier) {
  const parent = getTypeLabel(
    String(carrier?.appointmentParentGroup ?? '').trim()
  );
  const group = getTypeLabel(String(carrier?.appointmentGroup ?? '').trim());
  return [parent, group]
    .filter((label, index, all) => label && all.indexOf(label) === index)
    .join(' · ');
}

// shown instead of the plain empty message when the carrier type filter is what emptied a state
export const NO_CARRIERS_MATCH_FILTERS_MESSAGE =
  'No carriers match the selected filters.';

/**
 * state codes whose carrier list differs between two state maps
 * @param prevMap - `{ [stateCode]: { carriers } }` before the change
 * @param nextMap - `{ [stateCode]: { carriers } }` after the change
 */
export function getChangedStateCodes(prevMap, nextMap) {
  const changed = new Set();
  Object.keys(nextMap || {}).forEach(code => {
    const before = prevMap?.[code]?.carriers ?? [];
    const after = nextMap[code]?.carriers ?? [];
    const same =
      before.length === after.length &&
      before.every((name, index) => name === after[index]);
    if (!same) changed.add(code);
  });
  return changed;
}

// slug id for a carrier's parent-group tab
export function getParentGroupTabId(appointmentParentGroup) {
  return slugifyTabId(getParentGroupLabel(appointmentParentGroup));
}

// ordered, de-duped parent-group tab ids present in the carriers array (matches AppointmentTabs ordering)
export function getCarrierParentGroupTabIds(carriersArray) {
  const seen = new Set();
  const ids = [];
  (carriersArray || [])
    .filter(c => !!c.appointmentParentGroup)
    .forEach(c => {
      const id = getParentGroupTabId(c.appointmentParentGroup);
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
  return ids;
}

// carrier's "home" tab: appointed-carriers if appointed, else its parent-group tab
export function getCarrierHomeTabId(carrier, appointedCarrierNames) {
  if (!carrier) return APPOINTED_CARRIERS_TAB_ID;
  if (appointedCarrierNames?.has?.(carrier.name)) {
    return APPOINTED_CARRIERS_TAB_ID;
  }
  return getParentGroupTabId(carrier.appointmentParentGroup);
}

//whether a carrier shows in a given tab's grid (appointed-carriers or its parent-group; by-state/action-needed are not carrier-grid tabs)
export function carrierBelongsToTab(carrier, tabId, appointedCarrierNames) {
  if (!carrier || !tabId) return false;
  if (tabId === APPOINTED_CARRIERS_TAB_ID) {
    return !!appointedCarrierNames?.has?.(carrier.name);
  }
  if (tabId === 'by-state' || tabId === 'action-needed') return false;
  return getParentGroupTabId(carrier.appointmentParentGroup) === tabId;
}

export function getGroupedCarriersByParent(
  carriersArray,
  currentTab,
  appointedCarrierNames = null
) {
  let carriers = (carriersArray || [])
    .filter(c => !!c.appointmentParentGroup)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (
    currentTab &&
    currentTab !== APPOINTED_CARRIERS_TAB_ID &&
    currentTab !== 'by-state'
  ) {
    carriers = carriers.filter(
      c => getParentGroupTabId(c.appointmentParentGroup) === currentTab
    );
  }

  if (
    currentTab === APPOINTED_CARRIERS_TAB_ID &&
    appointedCarrierNames != null
  ) {
    carriers = carriers.filter(c => appointedCarrierNames.has(c.name));
  }

  const GROUP_PRIORITY = ['health', 'off market', 'ancillary'];

  const parentOrder = [];
  const byParent = {};
  carriers.forEach(c => {
    const p = c.appointmentParentGroup;
    if (!byParent[p]) {
      byParent[p] = { carriersWithoutSubgroup: [], subgroups: {} };
      parentOrder.push(p);
    }
    const groupVal = c.appointmentGroup;
    if (groupVal == null || groupVal === '') {
      byParent[p].carriersWithoutSubgroup.push(c);
    } else {
      if (!byParent[p].subgroups[groupVal])
        byParent[p].subgroups[groupVal] = [];
      byParent[p].subgroups[groupVal].push(c);
    }
  });

  parentOrder.sort((a, b) => {
    const ai = GROUP_PRIORITY.findIndex(p => a.toLowerCase().includes(p));
    const bi = GROUP_PRIORITY.findIndex(p => b.toLowerCase().includes(p));
    const aRank = ai === -1 ? GROUP_PRIORITY.length : ai;
    const bRank = bi === -1 ? GROUP_PRIORITY.length : bi;
    return aRank - bRank;
  });

  return parentOrder.map(parentGroupName => ({
    parentGroupName,
    carriersWithoutSubgroup: byParent[parentGroupName].carriersWithoutSubgroup,
    subgroups: Object.entries(byParent[parentGroupName].subgroups).map(
      ([groupName, carrierList]) => ({ groupName, carriers: carrierList })
    )
  }));
}
